/**
 * API and Socket URLs.
 * For native (iOS/Android) mobile apps via Capacitor, defaults to https://bazzef.com
 * unless overridden via environment variable or localStorage ('bazzef_api_url').
 * In desktop/web browser, defaults to relative origin.
 */

const isNativeApp = () => {
  if (typeof window === 'undefined') return false;
  if (window.Capacitor?.isNativePlatform?.()) return true;
  if (window.location.protocol === 'capacitor:') return true;
  if (window.location.origin === 'https://localhost') return true;
  return false;
};

const getApiBaseUrl = () => {
  // 1. Runtime override stored in localStorage (allows switching without rebuild)
  if (typeof window !== 'undefined') {
    try {
      const customUrl = localStorage.getItem('bazzef_api_url');
      if (customUrl && typeof customUrl === 'string' && customUrl.trim()) {
        return customUrl.trim().replace(/\/+$/, '');
      }
    } catch (e) {
      // ignore
    }
  }

  // 2. Build-time environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/+$/, '');
  }

  // 3. Native mobile app (Capacitor on Android/iOS)
  if (isNativeApp()) {
    return 'https://bazzef.com';
  }

  // 4. Web browser in desktop/mobile web
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return 'https://bazzef.com';
};

const API_BASE_URL = getApiBaseUrl();

const getSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL.replace(/\/+$/, '');
  }
  if (API_BASE_URL && API_BASE_URL !== 'https://localhost') {
    return API_BASE_URL;
  }
  if (isNativeApp()) {
    return 'https://bazzef.com';
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://bazzef.com';
};

const SOCKET_URL = getSocketUrl();

/**
 * Resolves a canonical public web URL for sharing/linking externally.
 * Ensures native apps (which run on capacitor://localhost or https://localhost)
 * produce public https://bazzef.com/... links rather than internal webview origins.
 * @param {string} [path=''] - Path to append (e.g. '/post/123')
 * @returns {string} Fully qualified web URL (e.g. 'https://bazzef.com/post/123')
 */
const getPublicWebUrl = (path = '') => {
  let base = 'https://bazzef.com';

  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (
      origin &&
      !isNativeApp() &&
      !origin.includes('localhost') &&
      !origin.startsWith('capacitor:')
    ) {
      base = origin;
    }
  }

  const cleanPath = path ? (path.startsWith('/') ? path : `/${path}`) : '';
  return `${base.replace(/\/+$/, '')}${cleanPath}`;
};

export { API_BASE_URL, SOCKET_URL, isNativeApp, getPublicWebUrl };
